// 3
// Path: frontend/src/components/QuickBooksSync/quickbooksEntities.js
// Inputs: None (constants and normalization utility functions)
// Outputs: Exported entity catalogs per target system, getTargetEntities, getEntityMeta, getQbEntityMeta, normalizeEntityName
// Summary: Canonical multi-target entity registry providing distinct entity catalogs per target system (QuickBooks, Xero, Passport), metadata lookup, and legacy entity normalization.

export const QUICKBOOKS_TARGET_ENTITIES = [
    {
        name: 'Customer',
        entity: 'Customer',
        label: 'Customer (Accounts Receivable)',
        category: 'Sales & AR',
        defaultTable: 'CUSFIL',
        icon: 'person',
        color: 'emerald',
        description: 'Customer master records, billing & shipping addresses, contact details, credit terms'
    },
    {
        name: 'Vendor',
        entity: 'Vendor',
        label: 'Vendor (Accounts Payable)',
        category: 'Expenses & AP',
        defaultTable: 'VENFIL',
        icon: 'storefront',
        color: 'sky',
        description: 'Vendor master records, pay-to addresses, payment terms, tax ID numbers'
    },
    {
        name: 'Item',
        entity: 'Item',
        label: 'Item (Products & Services)',
        category: 'Inventory & Sales',
        defaultTable: 'ITMFIL',
        icon: 'inventory_2',
        color: 'amber',
        description: 'Inventory items, non-inventory parts, service items, unit costs, pricing, income/cogs accounts'
    },
    {
        name: 'Account',
        entity: 'Account',
        label: 'Account (General Ledger)',
        category: 'Accounting & GL',
        defaultTable: 'CHARTF',
        icon: 'account_balance',
        color: 'purple',
        description: 'Chart of accounts, account numbers, account types, sub-types, classifications'
    },
    {
        name: 'Invoice',
        entity: 'Invoice',
        label: 'Invoice (Sales Orders & Billing)',
        category: 'Sales & AR',
        defaultTable: 'OEHDR',
        icon: 'receipt',
        color: 'blue',
        description: 'Customer invoices, sales orders, due dates, billing addresses, line items'
    },
    {
        name: 'Bill',
        entity: 'Bill',
        label: 'Bill (Vendor Invoices & AP)',
        category: 'Expenses & AP',
        defaultTable: 'POHDR',
        icon: 'request_quote',
        color: 'orange',
        description: 'Vendor bills, AP invoices, payment due dates, expense account allocations'
    },
    {
        name: 'Payment',
        entity: 'Payment',
        label: 'Payment (Customer Cash Receipts)',
        category: 'Sales & AR',
        defaultTable: 'ARTRN',
        icon: 'payments',
        color: 'teal',
        description: 'Customer payments, cash receipts, payment methods, bank deposit accounts'
    },
    {
        name: 'JournalEntry',
        entity: 'JournalEntry',
        label: 'Journal Entry (General Journal)',
        category: 'Accounting & GL',
        defaultTable: 'GLTRN',
        icon: 'menu_book',
        color: 'violet',
        description: 'General ledger journal vouchers, debits, credits, account adjustments'
    },
    {
        name: 'PurchaseOrder',
        entity: 'PurchaseOrder',
        label: 'Purchase Order',
        category: 'Purchasing',
        defaultTable: 'POHDR',
        icon: 'shopping_cart',
        color: 'indigo',
        description: 'Vendor purchase orders, purchasing lines, delivery dates, shipping instructions'
    },
    {
        name: 'Class',
        entity: 'Class',
        label: 'Class (Cost Center / Segment)',
        category: 'Accounting & GL',
        defaultTable: 'CTLFIL',
        icon: 'category',
        color: 'pink',
        description: 'Class tracking, profit centers, divisions, product lines'
    },
    {
        name: 'Department',
        entity: 'Department',
        label: 'Department (Location / Branch)',
        category: 'Accounting & GL',
        defaultTable: 'CTLFIL',
        icon: 'domain',
        color: 'rose',
        description: 'Department tracking, locations, stores, regional offices'
    },
    {
        name: 'CreditMemo',
        entity: 'CreditMemo',
        label: 'Credit Memo (Refunds / Returns)',
        category: 'Sales & AR',
        defaultTable: 'OEHDR',
        icon: 'assignment_return',
        color: 'cyan',
        description: 'Customer credit notes, sales refunds, returns, allowance memos'
    }
];

export const XERO_TARGET_ENTITIES = [
    {
        name: 'Contact',
        entity: 'Contact',
        label: 'Contact (Customer & Supplier)',
        category: 'Contacts',
        defaultTable: 'CUSFIL',
        icon: 'contacts',
        color: 'emerald',
        description: 'Xero contact records including customers, suppliers, addresses, phones, bank details'
    },
    {
        name: 'Invoice',
        entity: 'Invoice',
        label: 'Invoice (Sales Invoice - ACCREC)',
        category: 'Sales',
        defaultTable: 'OEHDR',
        icon: 'receipt',
        color: 'blue',
        description: 'Accounts receivable customer invoices, line items, unit amounts, account codes'
    },
    {
        name: 'Bill',
        entity: 'Bill',
        label: 'Bill (Purchase Bill - ACCPAY)',
        category: 'Purchases',
        defaultTable: 'POHDR',
        icon: 'request_quote',
        color: 'orange',
        description: 'Accounts payable supplier bills, expense lines, payment terms, due dates'
    },
    {
        name: 'Item',
        entity: 'Item',
        label: 'Item (Products & Services)',
        category: 'Inventory',
        defaultTable: 'ITMFIL',
        icon: 'inventory_2',
        color: 'amber',
        description: 'Xero tracked/untracked inventory items, sales/purchase prices, asset accounts'
    },
    {
        name: 'Account',
        entity: 'Account',
        label: 'Account (Chart of Accounts)',
        category: 'Accounting',
        defaultTable: 'CHARTF',
        icon: 'account_balance',
        color: 'purple',
        description: 'Xero chart of accounts, bank accounts, revenue, expenses, liabilities, equity'
    },
    {
        name: 'Payment',
        entity: 'Payment',
        label: 'Payment (Invoice & Bill Receipts)',
        category: 'Payments',
        defaultTable: 'ARTRN',
        icon: 'payments',
        color: 'teal',
        description: 'Xero payments allocated to customer invoices or supplier bills'
    },
    {
        name: 'BankTransaction',
        entity: 'BankTransaction',
        label: 'Bank Transaction (Spend & Receive)',
        category: 'Banking',
        defaultTable: 'ARTRN',
        icon: 'account_balance_wallet',
        color: 'cyan',
        description: 'Direct spend/receive money transactions into Xero bank feeds'
    },
    {
        name: 'PurchaseOrder',
        entity: 'PurchaseOrder',
        label: 'Purchase Order',
        category: 'Purchasing',
        defaultTable: 'POHDR',
        icon: 'shopping_cart',
        color: 'indigo',
        description: 'Xero supplier purchase orders, purchase items, delivery instructions'
    },
    {
        name: 'ManualJournal',
        entity: 'ManualJournal',
        label: 'Manual Journal (General Journal)',
        category: 'Accounting',
        defaultTable: 'GLTRN',
        icon: 'menu_book',
        color: 'violet',
        description: 'Xero general ledger manual journals, debits, credits, tracking category tags'
    },
    {
        name: 'CreditNote',
        entity: 'CreditNote',
        label: 'Credit Note (Sales Credit)',
        category: 'Sales',
        defaultTable: 'OEHDR',
        icon: 'assignment_return',
        color: 'pink',
        description: 'Xero customer credit notes, returns, credit allocations'
    },
    {
        name: 'TrackingCategory',
        entity: 'TrackingCategory',
        label: 'Tracking Category (Divisions / Regions)',
        category: 'Accounting',
        defaultTable: 'CTLFIL',
        icon: 'category',
        color: 'rose',
        description: 'Cost centers, departmental tracking categories, options and sub-divisions'
    }
];

export const PASSPORT_TARGET_ENTITIES = [
    {
        name: 'Customer',
        entity: 'Customer',
        label: 'Customer Master (CUSFIL)',
        category: 'Accounts Receivable',
        defaultTable: 'CUSFIL',
        icon: 'person',
        color: 'emerald',
        description: 'Passport customer master records, billing & shipping addresses, payment terms'
    },
    {
        name: 'Vendor',
        entity: 'Vendor',
        label: 'Vendor Master (VENFIL)',
        category: 'Accounts Payable',
        defaultTable: 'VENFIL',
        icon: 'storefront',
        color: 'sky',
        description: 'Passport vendor master records, remit-to addresses, discount terms, 1099 flags'
    },
    {
        name: 'Item',
        entity: 'Item',
        label: 'Item Master (ITMFIL)',
        category: 'Inventory',
        defaultTable: 'ITMFIL',
        icon: 'inventory_2',
        color: 'amber',
        description: 'Passport inventory item records, product classes, standard costs, pricing matrix'
    },
    {
        name: 'Account',
        entity: 'Account',
        label: 'Chart of Accounts (CHARTF)',
        category: 'General Ledger',
        defaultTable: 'CHARTF',
        icon: 'account_balance',
        color: 'purple',
        description: 'Passport chart of accounts, 4-part GL account structure (PC1, PC2, MAIN, SUB)'
    },
    {
        name: 'SalesOrder',
        entity: 'SalesOrder',
        label: 'Order Entry Header (OEHDR)',
        category: 'Order Entry',
        defaultTable: 'OEHDR',
        icon: 'receipt',
        color: 'blue',
        description: 'Passport sales order headers, customer orders, shipping dates, terms'
    },
    {
        name: 'SalesLine',
        entity: 'SalesLine',
        label: 'Order Entry Lines (OELIN)',
        category: 'Order Entry',
        defaultTable: 'OELIN',
        icon: 'list_alt',
        color: 'indigo',
        description: 'Passport sales order line items, ordered quantities, unit prices, discounts'
    },
    {
        name: 'PurchaseOrder',
        entity: 'PurchaseOrder',
        label: 'Purchase Order Header (POHDR)',
        category: 'Purchasing',
        defaultTable: 'POHDR',
        icon: 'shopping_cart',
        color: 'orange',
        description: 'Passport purchase order headers, vendors, delivery dates, warehouse locations'
    },
    {
        name: 'PurchaseLine',
        entity: 'PurchaseLine',
        label: 'Purchase Order Lines (POLIN)',
        category: 'Purchasing',
        defaultTable: 'POLIN',
        icon: 'list_alt',
        color: 'teal',
        description: 'Passport purchase order line items, order quantities, receiving status'
    },
    {
        name: 'ARTransaction',
        entity: 'ARTransaction',
        label: 'AR Transactions (ARTRN)',
        category: 'Accounts Receivable',
        defaultTable: 'ARTRN',
        icon: 'payments',
        color: 'cyan',
        description: 'Passport AR open items, cash receipts, discount entries, customer ledger'
    },
    {
        name: 'APTransaction',
        entity: 'APTransaction',
        label: 'AP Transactions (APTRN)',
        category: 'Accounts Payable',
        defaultTable: 'APTRN',
        icon: 'request_quote',
        color: 'violet',
        description: 'Passport AP vouchers, open invoices, disbursements, check numbers'
    },
    {
        name: 'GLTransaction',
        entity: 'GLTransaction',
        label: 'GL Transactions (GLTRN)',
        category: 'General Ledger',
        defaultTable: 'GLTRN',
        icon: 'menu_book',
        color: 'pink',
        description: 'Passport general ledger journal transactions, period postings, audit trails'
    }
];

export const TARGET_SYSTEM_ENTITIES = {
    QuickBooks: QUICKBOOKS_TARGET_ENTITIES,
    Xero: XERO_TARGET_ENTITIES,
    Passport: PASSPORT_TARGET_ENTITIES
};

/**
 * Returns the entity catalog array corresponding to the target data system
 * @param {string} targetSystem 
 * @returns {Array<object>}
 */
export function getTargetEntities(targetSystem) {
    const sys = String(targetSystem || 'QuickBooks').trim().toLowerCase();
    if (sys === 'xero') return XERO_TARGET_ENTITIES;
    if (sys === 'passport') return PASSPORT_TARGET_ENTITIES;
    return QUICKBOOKS_TARGET_ENTITIES;
}

// Map of common aliases/legacy plural forms to canonical target entity name
const ENTITY_ALIAS_MAP = {
    'customer': 'Customer',
    'customers': 'Customer',
    'vendor': 'Vendor',
    'vendors': 'Vendor',
    'item': 'Item',
    'items': 'Item',
    'account': 'Account',
    'accounts': 'Account',
    'invoice': 'Invoice',
    'invoices': 'Invoice',
    'order': 'Invoice',
    'orders': 'Invoice',
    'invoiceline': 'Invoice',
    'order lines': 'Invoice',
    'bill': 'Bill',
    'bills': 'Bill',
    'payment': 'Payment',
    'payments': 'Payment',
    'journalentry': 'JournalEntry',
    'journal entries': 'JournalEntry',
    'journal_entry': 'JournalEntry',
    'purchaseorder': 'PurchaseOrder',
    'purchase orders': 'PurchaseOrder',
    'purchase_order': 'PurchaseOrder',
    'class': 'Class',
    'classes': 'Class',
    'department': 'Department',
    'departments': 'Department',
    'location': 'Department',
    'locations': 'Department',
    'creditmemo': 'CreditMemo',
    'credit note': 'CreditNote',
    'contact': 'Contact',
    'contacts': 'Contact'
};

/**
 * Normalizes any entity string or entity object to canonical entity name
 * @param {string|object} rawName 
 * @returns {string} Canonical name, e.g. "Customer", "Invoice"
 */
export function normalizeEntityName(rawName) {
    if (!rawName) return 'Customer';
    const str = typeof rawName === 'string' ? rawName : (rawName.name || rawName.entity || String(rawName));
    const clean = String(str).trim().toLowerCase();
    return ENTITY_ALIAS_MAP[clean] || str;
}

/**
 * Retrieves metadata for a target entity within a specific target system
 * @param {string|object} entityName 
 * @param {string} targetSystem
 * @returns {object} Entity metadata object
 */
export function getEntityMeta(entityName, targetSystem = 'QuickBooks') {
    const canonical = normalizeEntityName(entityName);
    const catalog = getTargetEntities(targetSystem);
    const found = catalog.find(e => (e.entity || e.name).toLowerCase() === String(canonical).toLowerCase());
    if (found) return found;

    // Fallback search across all catalogs
    for (const cat of Object.values(TARGET_SYSTEM_ENTITIES)) {
        const anyFound = cat.find(e => (e.entity || e.name).toLowerCase() === String(canonical).toLowerCase());
        if (anyFound) return anyFound;
    }

    return {
        name: canonical,
        entity: canonical,
        label: canonical,
        category: 'General',
        defaultTable: 'CUSFIL',
        icon: 'table_chart',
        color: 'slate',
        description: `${canonical} records in ${targetSystem}`
    };
}

/**
 * Retrieves metadata for a target QB entity (backwards compatibility)
 * @param {string|object} entityName 
 * @returns {object} Entity metadata object
 */
export function getQbEntityMeta(entityName) {
    return getEntityMeta(entityName, 'QuickBooks');
}

